<template>
    <div>
      <MetaManager
        title="View All Gift Cards "
        description="Discover a wide selection of gift cards. Browse by category or explore all options to find the perfect gift for any occasion."
      />
      <div class="browse-all-deals">
        <CategoryFilter :categories="categories" @category-selected="filterGiftCards" :default-category="'all'" />
      </div>
      <div class="gift-card-list">
        <GiftCard
          v-for="giftCard in displayedGiftCards"
          :key="giftCard._id"
          :giftCardId="giftCard._id"
          :dealTitle="giftCard.dealtitle"
          :rating="formatRating(giftCard.rating)"
          :cardImage="giftCard.image_links"
          :originalPrice="giftCard.currentprice[0]"
          :discountPercentage="giftCard.discount[0]"
          :businessName= "giftCard.business_name"
          :businessAddress="giftCard.address"
          :optionsCount="giftCard.discount.length"
          :businessId="giftCard.business_id"
          :numberinstock="giftCard.numberinstock[0]"
          :isNewItem="giftCard.is_new_item"
          :hasNewInventory="giftCard.has_new_inventory"
        />
      </div>
      <!-- Remove Load More Button -->
    </div>
  </template>
  
  <script>
  import GiftCard from '@/components/GiftCard Components/GiftCard.vue';
  import CategoryFilter from '@/components/Frontend Nav Components/CategoryFilterBar.vue';
  import axios from 'axios';
  import MetaManager from '../../components/MetaManager.vue';
  
  export default {
    components: {
      CategoryFilter,
      GiftCard,
      MetaManager,
    },
    data() {
      return {
        categories: [
          { id: 'all', name: 'All' },
          { id: 'Restaurants', name: 'Restaurants' },
          { id: 'Beauty-spa', name: 'Beauty & Spas' },
          { id: 'Things-to-do', name: 'Things to do' },
          { id: 'Health-fitness', name: 'Health & Fitness' },
          { id: 'Automotive', name: 'Automotive' },
          { id: 'Home-services', name: 'Home Services' },
          { id: 'Retail', name: 'Retail' },
          { id: 'Technology', name: 'Technology' },
          { id: 'Other', name: 'Other' }
        ],
        giftCards: [],
        displayedGiftCards: [],
        selectedCategory: 'all',
        selectedLocation: localStorage.getItem('selectedLocation') || 'Stark County, Ohio',
      };
    },
    created() {
      this.applyCategoryFromRoute();
      this.loadGiftCards();
    },
    watch: {
      '$route.query.category'() {
        this.applyCategoryFromRoute();
      }
    },
    mounted() {
      // Listen for location changes from the navbar
      window.addEventListener('location-changed', (event) => {
        this.selectedLocation = event.detail.location;
        this.loadGiftCards(true); // Reload gift cards with new location
      });
      // Listen for mobile category bar events
      window.addEventListener('mobile-category-selected', this.handleMobileCategorySelected);
      // Ensure category from route is applied when component is mounted
      this.applyCategoryFromRoute();
    },
    beforeUnmount() {
      window.removeEventListener('mobile-category-selected', this.handleMobileCategorySelected);
    },
    methods: {
      handleMobileCategorySelected(event) {
        const category = event.detail;
        this.filterGiftCards(category);
      },
      applyCategoryFromRoute() {
        const category = this.$route.query.category;
        if (category && this.categories.some(c => c.id === category)) {
          this.selectedCategory = category;
        } else {
          this.selectedCategory = 'all';
        }
        // Trigger filter (will reload data)
        this.filterGiftCards({ id: this.selectedCategory });
      },
      async loadGiftCards(reset = false) {
        try {
          if (reset) {
            this.giftCards = [];
          }
          const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/get-gift-cards`, {
            params: {
              category: this.selectedCategory === 'all' ? '' : this.selectedCategory,
              location: this.selectedLocation,
              // No limit or offset needed as we want all gift cards
            }
          });
          this.giftCards = response.data;
          this.updateDisplayedGiftCards();
        } catch (error) {
          console.error('Error fetching gift cards:', error);
        }
      },
      filterGiftCards(category) {
        this.selectedCategory = category.id;
        this.loadGiftCards(true);
        this.$nextTick(() => {
          const el = document.querySelector('.gift-card-list');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => {
              window.scrollBy({ top: -80, left: 0, behavior: 'smooth' });
            }, 400);
          }
        });
      },
      updateDisplayedGiftCards() {
        this.displayedGiftCards = this.giftCards;
      },
      processDiscount(giftCard) {
        const { discount, currentprice, regularprice, discounttype } = giftCard;
        const isRegularPriceValid = regularprice && regularprice[0] !== 0;
        const isCurrentPriceValid = currentprice && currentprice[0] !== 0;
  
        return {
          discount: discount[0],
          regularprice: isRegularPriceValid ? regularprice : null,
          currentprice: isCurrentPriceValid ? currentprice : null,
          discounttype: discounttype[0]
        };
      },
      formatRating(rating) {
        if (rating === null || rating === undefined) return '0.00';
        return Number(rating).toFixed(1);
      },
      async addToShoppingCart(giftCardId) {
        const userId = this.getUserId();
        try {
          await axios.post(`${process.env.VUE_APP_API_URL}/api/shoppingCart/${userId}`, { giftCardId });
          this.$refs.giftCardCards.forEach(card => {
            if (card.giftCardId === giftCardId) {
              card.showMessage('Gift Card added to saved items');
            }
          });
        } catch (error) {
          if (error.response && error.response.status === 400) {
            this.$refs.giftCardCards.forEach(card => {
              if (card.giftCardId === giftCardId) {
                card.showMessage('Gift Card already saved');
              }
            });
          } else {
            this.$refs.giftCardCards.forEach(card => {
              if (card.giftCardId === giftCardId) {
                card.showMessage('Error adding gift card to shopping cart');
              }
            });
          }
        }
      },
      getUserId() {
        const user = JSON.parse(localStorage.getItem('user'));
        return user ? user.id : null;
      }
    },
  };
  </script>
  
  <style scoped>
.gift-card-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 28px 22px;
  margin-bottom: 28px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  padding: 0 24px;
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
}

.browse-all-deals {
  text-align: left;
  margin-left: 7%;
  margin-top: 50px;
  margin-bottom: 100px; /* Add more space below the filter bar */
  padding-bottom: 20px;
  font-family: 'Roboto', sans-serif;
}

/* Remove load-more-container styles */

button {
  padding: 15px 25px;
  font-size: 16px;
  cursor: pointer;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 17px;
  margin-bottom: 50px;
}

button:hover {
  background-color: #6E9F49;
}

.gift-card-list :deep(.gift-card) {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.gift-card-list :deep(.gift-card:hover) {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

@media (min-width: 1400px) {
  .gift-card-list {
    max-width: 1320px;
    gap: 28px 22px;
    padding: 0 40px;
  }
}

@media (max-width: 1200px) {
  .gift-card-list {
    max-width: 900px;
    gap: 24px 16px;
    padding: 0 16px;
  }
}

@media (max-width: 992px) {
  .gift-card-list {
    max-width: 650px;
    gap: 18px 10px;
    padding: 0 8px;
  }
}

@media (max-width: 768px) {
  .gift-card-list {
    max-width: 98vw;
    gap: 14px 6px;
    padding: 0 2vw;
  }
}

@media (max-width: 480px) {
  .gift-card-list {
    flex-direction: column;
    align-items: center;
    gap: 12px 0;
    padding: 0 2vw;
    max-width: 100vw;
  }
}

/* Restrict card width for better fit, do not affect internal structure */
.gift-card-list :deep(.gift-card) {
  max-width: 300px;
  min-width: 220px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}
</style>
